import Vue from 'vue';
import Vuex from 'vuex';
import VuexPersistence from 'vuex-persist';
import Heatmap from './modules/Heatmap';

Vue.use(Vuex);

const vuexLocal = new VuexPersistence({
  storage: window.localStorage,
  reducer: (state: any) => ({
    // add any states that need to be persisted beyond the current session
  }),
});

export default new Vuex.Store({
  state: {
    // prevents the query from running each time the search component is mounted
    queryParamFired: false,

    // district to full display name mapping. Populated on startup
    districts: {},
    subdistricts: {},

    // save the selected search results because Quasar tab switching destroy the components that are not visible
    districtResult: '',
    subdistrictResult: '',
    addressResult: '',
    parcelResult: '',

    // LayoutDefault
    rightDrawerOpen: false,

    // controls which component is shown in the panel
    rightDrawerContent: {
      print: false,
    },

    sketch: false, // visibility of sketch widget
    sketchLayer: null, // initalized as an empty GraphicsLayer
    searchResultsLayer: null, // initalized as an empty GraphicsLayer

    // Layers
    layers: [
      {
        name: 'NS Civic Address',
        type: 'FeatureLayer',
        url: 'https://services2.arcgis.com/hhEghZifbm8vjGX2/arcgis/rest/services/Civic_Addresses/FeatureServer/0',
        // url: 'https://nsgiwa.novascotia.ca/arcgis/rest/services/BASE/BASE_NS_CivicAddress_File_WM84/MapServer/0',
        minScale: 15000,
        active: false,
        outFields: ['PNTID'],
        labelsVisible: false,
        renderer: {
          type: 'simple',
          symbol: {
            type: 'simple-marker',
            outline: {
              width: 1,
              color: '#800000', // [115, 0, 0, 1]
            },
            size: 5,
            color: '#800000', // [230, 0, 0, 0.25],
          },
        },
      },
      {
        name: 'NS Parcel Data',
        type: 'FeatureLayer',
        url: 'https://nsgiwa2.novascotia.ca/arcgis/rest/services/PLAN/PLAN_NSPRD_WM84/MapServer/0',
        vectorLayer: 'https://tiles.arcgis.com/tiles/hhEghZifbm8vjGX2/arcgis/rest/services/Provincial_Parcels/VectorTileServer',
        vectorStyle: 'https://keithpower.maps.arcgis.com/sharing/rest/content/items/fd1e0c1b51d542f697d607f4220de9c3/resources/styles/root.json',
        active: false,
      },
      {
        name: 'Subdistricts',
        type: 'FeatureLayer',
        url: 'https://services2.arcgis.com/hhEghZifbm8vjGX2/ArcGIS/rest/services/NSAR_Subdistricts_Smoothed/FeatureServer/0',
        outFields: ['search', 'District_full', 'Region'],
        // vectorLayer: 'https://tiles.arcgis.com/tiles/hhEghZifbm8vjGX2/arcgis/rest/services/NSAR_Subdistricts_Tiles/VectorTileServer',
        active: false,
      },
      {
        name: 'Districts',
        type: 'FeatureLayer',
        url: 'https://services2.arcgis.com/hhEghZifbm8vjGX2/arcgis/rest/services/NSAR_Districts_Smoothed/FeatureServer/0',
        outFields: ['search', 'Region'],
        // vectorLayer: 'https://tiles.arcgis.com/tiles/hhEghZifbm8vjGX2/arcgis/rest/services/NSAR_Districts_Tiles/VectorTileServer',
        active: false,
      },
      {
        name: 'Regions',
        type: 'GeoJSONLayer',
        url: 'https://nsar.blob.core.windows.net/data/Regions.geojson',
        outFields: ['Region'],
        active: false,
        renderer: {
          type: 'unique-value',
          field: 'Region',
          uniqueValueInfos: [
            {
              value: 'Annapolis Valley Region',
              symbol: {
                type: 'simple-fill',
                color: [184, 254, 217, 0.6],
                style: 'solid',
                outline: {
                  color: [156, 156, 156],
                  width: 0.9,
                },
              },
            },
            {
              value: 'Cape Breton Region',
              symbol: {
                type: 'simple-fill',
                color: [255, 190, 232, 0.6],
                style: 'solid',
                outline: {
                  color: [156, 156, 156],
                  width: 0.9,
                },
              },
            },
            {
              value: 'Halifax Dartmouth Region',
              symbol: {
                type: 'simple-fill',
                color: [255, 170, 0, 0.6],
                style: 'solid',
                outline: {
                  color: [156, 156, 156],
                  width: 0.9,
                },
              },
            },
            {
              value: 'South Shore Region',
              symbol: {
                type: 'simple-fill',
                color: [239, 215, 252, 0.6],
                style: 'solid',
                outline: {
                  color: [156, 156, 156],
                  width: 0.9,
                },
              },
            },
            {
              value: 'Highland Region',
              symbol: {
                type: 'simple-fill',
                color: [230, 230, 0, 0.6],
                style: 'solid',
                outline: {
                  color: [156, 156, 156],
                  width: 0.9,
                },
              },
            },
            {
              value: 'Northern Region',
              symbol: {
                type: 'simple-fill',
                color: [252, 187, 196, 0.6],
                style: 'solid',
                outline: {
                  color: [156, 156, 156],
                  width: 0.9,
                },
              },
            },
            {
              value: 'Yarmouth Region',
              symbol: {
                type: 'simple-fill',
                color: [179, 179, 252, 0.6],
                style: 'solid',
                outline: {
                  color: [156, 156, 156],
                  width: 0.9,
                },
              },
            },
          ],
        },
      },
      {
        name: 'Community Boundaries',
        type: 'FeatureLayer',
        url: 'https://nsgiwa.novascotia.ca/arcgis/rest/services/BND/BND_NS_Community_Bndys_UT83/MapServer/0',
        outFields: ['GSA_NAME '],
        active: false,
        labelsVisible: true,
        renderer: {
          type: 'simple',
          symbol: {
            type: 'simple-fill',
            outline: {
              width: 0.8,
              color: '#D4A61E',
            },
            color: [0, 0, 0, 0.0],
          },
        },
        labelClass: {
          symbol: {
            type: 'text',
            color: '#9E7106',
            font: {
              size: 9,
            },
          },
          labelExpressionInfo: {
            expression: '$feature.GSA_NAME',
          },
          minScale: 300000,
          maxScale: 0,
        },
      },
      {
        name: 'Schools',
        type: 'FeatureLayer',
        url: 'https://services2.arcgis.com/hhEghZifbm8vjGX2/arcgis/rest/services/HRCE_Schools/FeatureServer/0',
        outFields: ['SchoolName'],
        active: false,
      },
      {
        name: 'School Catchments - English Elementary',
        type: 'FeatureLayer',
        url: 'https://services2.arcgis.com/11XBiaBYA9Ep0yNJ/arcgis/rest/services/HRCE_English_Program_Elementary/FeatureServer/0',
        outFields: ['SCH_NAME'],
        active: false,
        renderer: {
          type: 'simple',
          symbol: {
            type: 'simple-fill',
            outline: {
              width: 1,
              color: '#000000',
            },
            color: [255, 237, 160, 0.6],
          },
        },
      },
      {
        name: 'School Catchments - Early-French Elementary',
        type: 'FeatureLayer',
        url: 'https://services2.arcgis.com/11XBiaBYA9Ep0yNJ/arcgis/rest/services/HRCE_Early_French_Immersion_Elementary/FeatureServer/0',
        outFields: ['SCH_NAME'],
        active: false,
        renderer: {
          type: 'simple',
          symbol: {
            type: 'simple-fill',
            outline: {
              width: 1,
              color: '#000000',
            },
            color: [254, 217, 118, 0.6],
          },
        },
      },
      {
        name: 'School Catchments - English Junior High',
        type: 'FeatureLayer',
        url: 'https://services2.arcgis.com/11XBiaBYA9Ep0yNJ/arcgis/rest/services/HRCE_English_Program_Junior_High/FeatureServer/0',
        outFields: ['SCH_NAME'],
        active: false,
        renderer: {
          type: 'simple',
          symbol: {
            type: 'simple-fill',
            outline: {
              width: 1,
              color: '#000000',
            },
            color: [254, 178, 76, 0.4],
          },
        },
      },
      {
        name: 'School Catchments - Early-French Junior High',
        type: 'FeatureLayer',
        url: 'https://services2.arcgis.com/11XBiaBYA9Ep0yNJ/arcgis/rest/services/HRCE_Early_French_Immersion_Junior_High/FeatureServer/0',
        outFields: ['SCH_NAME'],
        active: false,
        renderer: {
          type: 'simple',
          symbol: {
            type: 'simple-fill',
            outline: {
              width: 1,
              color: '#000000',
            },
            color: [253, 141, 60, 0.4],
          },
        },
      },
      {
        name: 'School Catchments - Late-French Junior High',
        type: 'FeatureLayer',
        url: 'https://services2.arcgis.com/11XBiaBYA9Ep0yNJ/arcgis/rest/services/HRCE_Late_French_Immersion_Junior_High/FeatureServer/0',
        outFields: ['SCH_NAME'],
        active: false,
        renderer: {
          type: 'simple',
          symbol: {
            type: 'simple-fill',
            outline: {
              width: 1,
              color: '#000000',
            },
            color: [253, 141, 60, 0.4],
          },
        },
      },
      {
        name: 'School Catchments - English High',
        type: 'FeatureLayer',
        url: 'https://services2.arcgis.com/11XBiaBYA9Ep0yNJ/arcgis/rest/services/HRCE_English_Program_High/FeatureServer/0',
        outFields: ['SCH_NAME'],
        active: false,
        renderer: {
          type: 'simple',
          symbol: {
            type: 'simple-fill',
            outline: {
              width: 1,
              color: '#000000',
            },
            color: [252, 78, 42, 0.4],
          },
        },
      },
      {
        name: 'School Catchments - Early-French High',
        type: 'FeatureLayer',
        url: 'https://services2.arcgis.com/11XBiaBYA9Ep0yNJ/arcgis/rest/services/HRCE_Early_French_Immersion_High/FeatureServer/0',
        outFields: ['SCH_NAME'],
        active: false,
        renderer: {
          type: 'simple',
          symbol: {
            type: 'simple-fill',
            outline: {
              width: 1,
              color: '#000000',
            },
            color: [227, 26, 28, 0.4],
          },
        },
      },
      {
        name: 'School Catchments - Late-French High',
        type: 'FeatureLayer',
        url: 'https://services2.arcgis.com/11XBiaBYA9Ep0yNJ/arcgis/rest/services/HRCE_Late_French_Immersion_High/FeatureServer/0',
        outFields: ['SCH_NAME'],
        active: false,
        renderer: {
          type: 'simple',
          symbol: {
            type: 'simple-fill',
            outline: {
              width: 1,
              color: '#000000',
            },
            color: [177, 0, 38, 0.4],
          },
        },
      },
    ],
    basemaps: [
      {name: 'Streets', url: 'streets-vector', active: true},
      {name: 'Satellite (GeoNova)', url: 'https://nsgiwa.novascotia.ca/arcgis/rest/services/BASE/BASE_NSODB_10k_WM84/MapServer', active: false},
      {name: 'Satellite', url: 'satellite', active: false},
      {name: 'Topographic', url: 'topo-vector', active: false},
      {name: 'Greyscale', url: 'gray-vector', active: false},
      {name: 'Dark Greyscale', url: 'dark-gray-vector', active: false},
    ],

    // grey
    vectorStyle: 'https://keithpower.maps.arcgis.com/sharing/rest/content/items/fd1e0c1b51d542f697d607f4220de9c3/resources/styles/root.json',
    // red
    vectorStyleAlt: 'https://keithpower.maps.arcgis.com/sharing/rest/content/items/b4de66a5a9324411bd659672d24b8467/resources/styles/root.json',

    // highlighted graphic feature
    searchGraphic: null,

    // EsriMap
    map: null,
    mapView: null,
    center: null,
    zoom: null,

    // Text for null zones
    nullZoneText: 'N/A',

  },

  mutations: {
    SET_DISTRICT_RESULT(state: any, result: string) {
      state.districtResult = result;
    },
    SET_SUBDISTRICT_RESULT(state: any, result: string) {
      state.subdistrictResult = result;
    },
    SET_ADDRESS_RESULT(state: any, result: string) {
      state.addressResult = result;
    },
    SET_PARCEL_RESULT(state: any, result: string) {
      state.parcelResult = result;
    },
    CLEAR_ALL_RESULTS(state: any) {
      state.districtResult = '';
      state.subdistrictResult = '';
      state.addressResult = '';
      state.parcelResult = '';
    },
    QUERY_PARAM_FIRED(state: any) {
      state.queryParamFired = true;
    },
    SET_BASEMAP(state: any, basemap: any) {
      state.map.basemap = basemap;
      const parcelLayer = state.map.layers.find((layer: __esri.Layer) => {
        return layer.title === 'NS Parcel Data' + '_vector';
      });
      if (basemap.title === 'Satellite (GeoNova)' || basemap === 'satellite') {
        parcelLayer.loadStyle(state.vectorStyleAlt);
      } else {
        parcelLayer.loadStyle(state.vectorStyle);
      }
      state.basemaps.forEach((item: any, index: number) => {
          if (item.url === basemap) {
              item.active = true;
          } else {
              item.active = false;
          }
      });
    },
    TOGGLE_LAYER(state: any, layer: any) {
      // this is not clear, should be using ids
      state.map.layers.forEach((item: any, index: number) => {
        if (item.type !== 'graphics') {
          if (layer.hasOwnProperty('vectorLayer')) {
            if (layer.vectorLayer === item.parsedUrl.path) {
              item.visible = !item.visible;
            }
          } else {
            if (layer.url === item.parsedUrl.path) {
              item.visible = !item.visible;
            }
          }
        }
      });
    },
    DRAWER_TOGGLE_RIGHT(state: any, content: string) {
      for (const k of Object.keys(state.rightDrawerContent)) {
        if (k === content) {
          state.rightDrawerContent[k] = !state.rightDrawerContent[k];
        } else {
          state.rightDrawerContent[k] = false;
        }
      }
      let openSesame = false;
      for (const v of Object.values(state.rightDrawerContent)) {
        if (v) {
          openSesame = true;
        }
      }
      state.rightDrawerOpen = openSesame;
    },
    SKETCH_TOGGLE(state: any) {
      state.sketch = !state.sketch;
    },
    SET_MAP(state: any, payload: any) {
      state.map = payload;
    },
    SET_MAP_VIEW(state: any, payload: any) {
      state.mapView = payload;
    },
  },

  actions: {
    setDistrictResult(context, result) {
      context.commit('SET_DISTRICT_RESULT', result);
    },
    setSubdistrictResult(context, result) {
      context.commit('SET_SUBDISTRICT_RESULT', result);
    },
    setAddressResult(context, result) {
      context.commit('SET_ADDRESS_RESULT', result);
    },
    setParcelResult(context, result) {
      context.commit('SET_PARCEL_RESULT', result);
    },
    clearAllResults(context) {
      context.commit('CLEAR_ALL_RESULTS');
    },
    queryParamFired(context) {
      context.commit('QUERY_PARAM_FIRED');
    },
    setBasemap(context, basemap) {
      context.commit('SET_BASEMAP', basemap);
    },
    toggleLayer(context, layer) {
      context.commit('TOGGLE_LAYER', layer);
    },
    setMap(context, payload) {
      context.commit('SET_MAP', payload);
    },
    setMapView(context, payload) {
      context.commit('SET_MAP_VIEW', payload);
    },
    drawerToggleRight(context, content) {
      context.commit('DRAWER_TOGGLE_RIGHT', content);
    },
    sketchToggle(context) {
      context.commit('SKETCH_TOGGLE');
    },
    clearDrawing(context) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    initSearch(context) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    initPopup(context) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    aboutAction(context) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
    notify(context, message) {
      // no state/mutations, this is only used to subscribe to actions in other components
    },
  },

  modules: {
    heatmap: Heatmap,
  },

  plugins: [vuexLocal.plugin],
});
